import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import ReplayOutlined from '@mui/icons-material/ReplayOutlined';
import Typography from '@mui/material/Typography';
import { theme } from '@omnigenbiodata/ui';

const errorTint = theme.palette.error.lighter;

interface LinkingFailedPanelProps {
  participant: {
    participantRef?: string | null;
    firstName?: string | null;
    surname?: string | null;
  } | null;
  sample: {
    sampleBarcode?: string | null;
    linkDate?: string | null;
  } | null;
  errorMessage?: string | null;
  onStartAgain: () => void;
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2,
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-of-type': {
          borderBottom: 0,
        },
      }}
    >
      <Typography variant="body1" component="dt" color="text.secondary" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>

      <Typography
        variant="body1"
        component="dd"
        sx={{
          m: 0,
          textAlign: 'right',
          fontWeight: 700,
          color: 'primary.main',
          overflowWrap: 'anywhere',
        }}
      >
        {value || 'Not recorded'}
      </Typography>
    </Box>
  );
}

function FailedIcon() {
  return (
    <Box
      sx={{
        width: 170,
        height: 170,
        borderRadius: '50%',
        bgcolor: errorTint,
        border: 2,
        borderColor: 'error.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      aria-hidden="true"
    >
      <ErrorOutlineOutlined color="error" sx={{ fontSize: 112 }} />
    </Box>
  );
}

function LinkingFailedPanel({ participant, sample, errorMessage, onStartAgain }: LinkingFailedPanelProps) {
  const participantName = [participant?.firstName, participant?.surname].filter(Boolean).join(' ');

  return (
    <Grid container spacing={4} alignItems="center">
      <Grid item xs={12} md={4}>
        <Box
          sx={{
            minHeight: 320,
            borderRadius: 4,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'error.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
          }}
        >
          <FailedIcon />
        </Box>
      </Grid>

      <Grid item xs={12} md={8}>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            color: 'primary.main',
            mb: 2,
            lineHeight: 1.1,
          }}
        >
          Linking failed
        </Typography>

        <Typography
          variant="h5"
          component="p"
          color="text.secondary"
          sx={{
            lineHeight: 1.4,
            maxWidth: 760,
          }}
        >
          The participant and sample kit could not be linked. Please check the details and try again.
        </Typography>

        {errorMessage && (
          <Box
            sx={{
              mt: 4,
              p: 3,
              borderRadius: 3,
              bgcolor: errorTint,
              border: 1,
              borderColor: 'error.main',
            }}
          >
            <Typography
              variant="h6"
              component="p"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                mb: 1,
              }}
            >
              Error details
            </Typography>

            <Typography variant="body1" component="p" color="text.secondary" sx={{ lineHeight: 1.45 }}>
              {errorMessage}
            </Typography>
          </Box>
        )}

        <Paper
          elevation={2}
          sx={{
            mt: 4,
            border: 1,
            borderColor: 'error.main',
            bgcolor: errorTint,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ height: 8, bgcolor: 'error.main' }} />

          <Box p={3}>
            <Box component="dl" sx={{ m: 0 }}>
              <DetailRow label="Participant Ref" value={participant?.participantRef} />
              <DetailRow label="Participant" value={participantName} />
              <DetailRow label="Sample Barcode" value={sample?.sampleBarcode} />

              {sample?.linkDate && <DetailRow label="Link Date" value={formatDateTime(sample.linkDate)} />}
            </Box>
          </Box>
        </Paper>

        <Box
          sx={{
            mt: 4,
            p: 3,
            borderRadius: 3,
            bgcolor: 'warning.lighter',
            border: 1,
            borderColor: 'warning.main',
          }}
        >
          <Typography
            variant="h6"
            component="p"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
            }}
          >
            Do not assume the sample kit has been linked. Please retry the linking process or escalate for support if
            the issue continues.
          </Typography>
        </Box>

        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          startIcon={<ReplayOutlined />}
          onClick={onStartAgain}
          sx={{
            mt: 4,
            minHeight: 72,
            fontSize: '1.25rem',
            fontWeight: 700,
          }}
        >
          Try again
        </Button>
      </Grid>
    </Grid>
  );
}

export default LinkingFailedPanel;
